import React from "react";
import Image from "next/image";
import { cn } from "@/lib/utils/cn";

interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  fallback?: string;
  size?: "sm" | "default" | "lg" | "xl";
  status?: "online" | "offline" | "away" | "busy";
}

export function Avatar({ src, alt, fallback, size = "default", status, className, ...props }: AvatarProps) {
  const sizeClasses: Record<string, string> = {
    sm: "h-8 w-8 text-xs",
    default: "h-10 w-10 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-16 w-16 text-lg",
  };

  const statusSizes: Record<string, string> = {
    sm: "h-2 w-2 right-0 bottom-0",
    default: "h-2.5 w-2.5 right-0 bottom-0",
    lg: "h-3 w-3 right-0.5 bottom-0.5",
    xl: "h-3.5 w-3.5 right-0.5 bottom-0.5",
  };

  const statusColors: Record<string, string> = {
    online: "bg-success",
    offline: "bg-neutral-300",
    away: "bg-warning",
    busy: "bg-destructive",
  };

  const initials = fallback
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() ?? "?";

  return (
    <div
      className={cn(
        "relative flex shrink-0 overflow-hidden rounded-full bg-muted text-foreground ring-2 ring-background",
        sizeClasses[size],
        className
      )}
      {...props}
    >
      {src ? (
        <Image src={src} alt={alt ?? "Avatar"} fill className="object-cover" unoptimized />
      ) : (
        <div className="flex h-full w-full items-center justify-center font-medium">
          {initials}
        </div>
      )}
      {status && (
        <span
          className={cn(
            "absolute rounded-full border-2 border-background",
            statusSizes[size],
            statusColors[status]
          )}
          aria-label={status}
        />
      )}
    </div>
  );
}
Avatar.displayName = "Avatar";
